import { googleAuth } from "@/apis/v1/user";
import usePersistSession from "@/hooks/usePersistSession";
import {
  IGoogleAuthResponse,
  IGoogleNeedsSignup,
} from "@/modules/User/user.interface";
import routes from "@/routes";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

type ApiError = { errMsg?: string };

const POPUP_FAILED = "Google sign-in was cancelled or failed. Please try again.";
export const GENERIC_ERROR = "Something went wrong. Please try again.";

/** Router state handed from the Google button to /register/google. Never put the ticket in the URL. */
export interface GoogleSignupState {
  signupTicket: string;
  profile: IGoogleNeedsSignup["profile"];
}

function needsSignup(res: IGoogleAuthResponse): res is IGoogleNeedsSignup {
  return (res as IGoogleNeedsSignup).needsSignup === true;
}

/**
 * Google credential → `POST /auth/google`.
 * Existing user → the same `persistSession` path as password login, so every
 * redirect/guard behaves identically. New user → complete-signup step.
 */
export default function useGoogleAuth() {
  const persistSession = usePersistSession();
  const navigate = useNavigate();
  // cacheTime 0: don't keep the Google ID token (mutation variables) in the
  // MutationCache after the request settles.
  const { mutateAsync, isLoading, error, reset: resetMutation } = useMutation(
    googleAuth,
    { cacheTime: 0 }
  );
  const [popupError, setPopupError] = useState<string | null>(null);
  // Synchronous guard: `isLoading` only flips after a re-render.
  const inFlight = useRef(false);

  const onCredential = useCallback(
    async (credential: string) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setPopupError(null);
      try {
        const res = (await mutateAsync({
          credential,
        })) as unknown as IGoogleAuthResponse;
        if (needsSignup(res)) {
          const state: GoogleSignupState = {
            signupTicket: res.signupTicket,
            profile: res.profile,
          };
          navigate(routes.REGISTER_GOOGLE, { state });
          return;
        }
        persistSession(res);
      } catch {
        // Surfaced via `errMsg` from the mutation's error state.
      } finally {
        inFlight.current = false;
      }
    },
    [mutateAsync, navigate, persistSession]
  );

  const onError = useCallback(() => {
    resetMutation();
    setPopupError(POPUP_FAILED);
  }, [resetMutation]);

  const reset = useCallback(() => {
    resetMutation();
    setPopupError(null);
  }, [resetMutation]);

  return {
    onCredential,
    onError,
    reset,
    isLoading,
    errMsg:
      popupError ??
      (error ? (error as ApiError).errMsg || GENERIC_ERROR : undefined),
  };
}
